import { useId, useRef, useState, type FormEvent } from "react";

import { proposeSchedule, rescheduleAppointment } from "../../lib/api/conversation";
import { isValidTime, maxScheduleDate, minutesFromTime, todayInSAST } from "../../lib/format";
import type { Appointment } from "../../lib/types/models";
import {
  Banner,
  Button,
  focusFirstInvalid,
  Modal,
  TextArea,
  TextField,
} from "@ui/react";
import { errorMessage } from "../../lib/errorMessage";

/**
 * Proposes an appointment on a conversation — or, given an existing
 * `appointment`, reschedules it (the old appointment becomes `Rescheduled`
 * server-side and this posts a new one).
 *
 * Every value here is a timezone-NAIVE wall-clock string and is sent exactly as
 * the browser produced it: <input type="date"> already emits YYYY-MM-DD and
 * <input type="time"> already emits HH:MM. Building either from a Date would
 * convert to UTC and shift the appointment by the viewer's offset — invisible
 * in Johannesburg (UTC+2, no DST), wrong everywhere else.
 *
 * The call also flips the job Accepted -> Scheduled server-side, so the caller
 * refreshes anything it is showing about that job.
 */
export function ProposeScheduleModal({
  conversationId,
  appointment,
  onClose,
  onProposed,
}: {
  conversationId: string;
  /** Present only when rescheduling an existing appointment. */
  appointment?: Appointment;
  onClose: () => void;
  onProposed: (appointment: Appointment) => void;
}) {
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const isReschedule = Boolean(appointment);

  const [date, setDate] = useState(appointment?.scheduledDate ?? "");
  const [startTime, setStartTime] = useState(appointment?.startTime ?? "");
  const [endTime, setEndTime] = useState(appointment?.endTime ?? "");
  const [notes, setNotes] = useState(appointment?.notes ?? "");

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!date) errors["date"] = "Choose a date";
    else if (date < todayInSAST()) errors["date"] = "Choose a date from today onwards.";
    else if (date > maxScheduleDate()) errors["date"] = "Pick a date within the next 7 days.";
    if (!startTime) errors["startTime"] = "Choose a start time";
    if (startTime && !isValidTime(startTime)) errors["startTime"] = "Use a 24-hour time, e.g. 09:00";
    if (endTime) {
      if (!isValidTime(endTime)) {
        errors["endTime"] = "Use a 24-hour time, e.g. 16:30";
      } else if (isValidTime(startTime) && minutesFromTime(endTime) <= minutesFromTime(startTime)) {
        errors["endTime"] = "The end time must be after the start time";
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        scheduledDate: date,
        startTime,
        ...(endTime ? { endTime } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      const result = appointment
        ? await rescheduleAppointment(conversationId, appointment.appointmentId, body)
        : await proposeSchedule(conversationId, body);
      onProposed(result);
    } catch (error) {
      setFormError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isReschedule ? "Reschedule this appointment" : "Propose a time"}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={submitting}>
            {isReschedule ? "Send reschedule" : "Send proposal"}
          </Button>
        </>
      }
    >
      <form className="form" id={formId} ref={formRef} onSubmit={handleSubmit} noValidate>
        {formError ? (
          <Banner
            tone="danger"
            title={isReschedule ? "Couldn't reschedule" : "Couldn't propose that time"}
          >
            {errorMessage(formError)}
          </Banner>
        ) : null}

        <TextField
          label="Date"
          type="date"
          required
          min={todayInSAST()}
          max={maxScheduleDate()}
          hint="Within the next 7 days — the homeowner's card hold expires after that."
          value={date}
          error={fieldErrors["date"]}
          onChange={(event) => setDate(event.target.value)}
        />

        <div className="field-grid">
          <TextField
            label="Start time"
            type="time"
            required
            value={startTime}
            error={fieldErrors["startTime"]}
            onChange={(event) => setStartTime(event.target.value)}
          />
          <TextField
            label="End time"
            type="time"
            hint="Optional — leave blank if you're not sure yet."
            value={endTime}
            error={fieldErrors["endTime"]}
            onChange={(event) => setEndTime(event.target.value)}
          />
        </div>

        <TextArea
          label="Notes"
          rows={3}
          hint="Optional. Anything the homeowner should have ready, or where to park."
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </form>
    </Modal>
  );
}
